import React, { useEffect, useState } from "react";
import { X, Mail, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const KINDS = [
  { id: "received", label: "Registration received" },
  { id: "confirmed", label: "You're confirmed" },
];
const EMPTY = { subject: "", welcome: "", arrival_note: "" };
const FORM_BY_TYPE = { training: "teacher_training", retreat: "destination_retreat", event: "workshop_class" };
const INTAKE_FORMS = ["teacher_training", "destination_retreat", "day_retreat", "workshop_class"];
// Mirrors OfferingDetail: offerings without an intake form (e.g. courses) confirm on purchase, so no "received" email.
const hasReceivedEmail = (o) => INTAKE_FORMS.includes(o.registration_form || FORM_BY_TYPE[o.type]);

const initialDrafts = (offering) => {
  const t = offering.email_templates || {};
  return { received: { ...EMPTY, ...(t.received || {}) }, confirmed: { ...EMPTY, ...(t.confirmed || {}) } };
};

const inputCls = "w-full bg-white border border-aviana-border rounded-xl px-4 py-3 text-sm text-aviana-ink outline-none focus:border-aviana-plum transition-colors";

function DraftField({ label, hint, value, onChange, placeholder, rows, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase text-aviana-plum mb-2">{label}</span>
      {rows ? (
        <textarea rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
      ) : (
        <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
      )}
      {hint && <span className="block text-[11px] text-aviana-muted mt-1.5 leading-relaxed">{hint}</span>}
    </label>
  );
}

function PreviewPane({ preview, loading }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col bg-aviana-surface rounded-2xl border border-aviana-border overflow-hidden">
      <div className="px-5 py-3 border-b border-aviana-border bg-white flex items-center gap-3">
        <Mail size={15} className="text-aviana-plum flex-shrink-0" />
        <p className="text-sm text-aviana-ink truncate" data-testid="email-preview-subject">
          <span className="text-aviana-muted mr-2">Subject:</span>{preview?.subject || "…"}
        </p>
        {loading && <span className="ml-auto text-[10px] tracking-[0.2em] uppercase text-aviana-muted">Updating…</span>}
      </div>
      <iframe title="Email preview" srcDoc={preview?.html || ""} className="flex-1 w-full min-h-[60vh] bg-white" data-testid="email-preview-frame" />
    </div>
  );
}

export const EmailPreviewModal = ({ offering, onClose, onSaved }) => {
  const hasReceived = hasReceivedEmail(offering);
  const kinds = hasReceived ? KINDS : KINDS.filter((k) => k.id === "confirmed");
  const [kind, setKind] = useState(hasReceived ? "received" : "confirmed");
  const [drafts, setDrafts] = useState(() => initialDrafts(offering));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const draft = drafts[kind];
  const defaults = preview?.defaults || {};

  useEffect(() => {
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const { data } = await api.post(`/admin/offerings/${offering.id}/email-preview`, { kind, ...draft });
        setPreview(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load preview"); }
      setLoading(false);
    }, 400);
    return () => clearTimeout(h);
  }, [offering.id, kind, draft]);

  const update = (field, v) => setDrafts((d) => ({ ...d, [kind]: { ...d[kind], [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/offerings/${offering.id}`, { email_templates: drafts });
      toast.success("Email copy saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-aviana-ink/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="email-preview-modal">
      <div className="bg-aviana-cream rounded-2xl w-full max-w-7xl max-h-[94vh] flex flex-col shadow-2xl">
        <header className="px-7 py-5 border-b border-aviana-border flex items-start justify-between gap-4">
          <div>
            <p className="overline-plum mb-1">Confirmation emails</p>
            <h2 className="font-display text-2xl text-aviana-ink">{offering.title}</h2>
          </div>
          <button onClick={onClose} className="text-aviana-muted hover:text-aviana-ink transition-colors" data-testid="email-preview-close"><X size={20} /></button>
        </header>
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row gap-6 p-6">
          <div className="lg:w-[380px] flex-shrink-0 space-y-5 overflow-y-auto pr-1">
            <div className="flex gap-2 bg-white rounded-full p-1 border border-aviana-border" data-testid="email-kind-toggle">
              {kinds.map((k) => (
                <button key={k.id} onClick={() => setKind(k.id)} data-testid={`email-kind-${k.id}`}
                  className={`flex-1 rounded-full py-2 text-[10px] tracking-[0.16em] uppercase transition-colors ${kind === k.id ? "bg-aviana-plum text-aviana-cream" : "text-aviana-muted hover:text-aviana-ink"}`}>
                  {k.label}
                </button>
              ))}
            </div>
            <DraftField label="Subject line" value={draft.subject} onChange={(v) => update("subject", v)}
              placeholder={defaults.subject} testid="email-subject-input" />
            <DraftField label="Welcome message" rows={5} value={draft.welcome} onChange={(v) => update("welcome", v)}
              placeholder={defaults.welcome || "Optional — add a personal welcome message"}
              hint="Leave a blank line between paragraphs." testid="email-welcome-input" />
            <DraftField label="Before you arrive (optional)" rows={4} value={draft.arrival_note} onChange={(v) => update("arrival_note", v)}
              placeholder="e.g. Bring a yoga mat, a blanket and a water bottle." testid="email-arrival-input" />
            <p className="text-[11px] text-aviana-muted leading-relaxed">
              Use <code className="text-aviana-plum">{"{first_name}"}</code> and <code className="text-aviana-plum">{"{title}"}</code> to personalise. Blank fields use the default text shown in grey. Date, time, location, cancellation policy and Bliss Tent parking fill in automatically.
            </p>
            <button onClick={() => setDrafts((d) => ({ ...d, [kind]: { ...EMPTY } }))} className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-aviana-muted hover:text-aviana-plum transition-colors" data-testid="email-reset-default">
              <RotateCcw size={13} /> Reset this email to default
            </button>
          </div>
          <PreviewPane preview={preview} loading={loading} />
        </div>
        <footer className="px-7 py-4 border-t border-aviana-border flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-secondary !py-2 !px-5 !text-[11px]" data-testid="email-preview-cancel">Close</button>
          <button onClick={save} disabled={saving} className="btn-gold !py-2.5 !px-6 !text-[11px]" data-testid="email-preview-save">
            {saving ? "Saving…" : "Save email copy"}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default EmailPreviewModal;
